import { useMemo } from "react";
import { Box, Text } from "@salestrak/ui";

import {
  EntityGroupTag,
  EntitySelectionHover,
  EntityTag,
} from "#app/components/entity";
import { useMemberDirectory } from "#app/lib/data";
import type { Omittable } from "@salestrak/ui";

/**
 * The at-rest face of a `member` custom-field value: the members themselves,
 * not a count. Ids resolve through the member directory, so a member outside
 * the bootstrap page still gets an avatar once the directory answers.
 *
 * The shape follows the surface the value sits on:
 * - `surface="cell"` — a list or table cell is the 20px avatar stack whatever
 *   the count. A lone member is a lone avatar, never avatar + name.
 * - `surface="property"` — a drawer property row names ONE member (avatar +
 *   label) and collapses two or more to the avatar-only stack.
 * The hover preview carries the identity in every case.
 *
 * Renders inside the field's selector trigger, so nothing here owns a press:
 * `interactive={false}` throughout, with the hover wrapper restoring the lone
 * chip's preview (the selector-trigger pattern).
 */
export function CustomFieldMemberValue({
  value,
  surface,
  hoverDisabled,
}: Omittable<{
  value: unknown;
  surface: "cell" | "property";
  /** Suppress the hover preview (e.g. while the owning picker is open). */
  hoverDisabled?: boolean;
}>) {
  const ids = useMemo(
    () =>
      (Array.isArray(value) ? value : value == null ? [] : [value]).filter(
        (id): id is string => typeof id === "string" && id !== "",
      ),
    [value],
  );
  const { request, entityOf } = useMemberDirectory();
  request(ids);
  const entities = useMemo(
    () => ids.flatMap((id) => entityOf(id) ?? []),
    [ids, entityOf],
  );

  if (ids.length === 0) {
    return (
      <Text size="sm" variant="muted">
        -
      </Text>
    );
  }
  // Nothing resolved yet: hold the row's height with a muted count rather than
  // collapsing it while the directory answers.
  if (entities.length === 0) {
    return (
      <Text size="sm" variant="muted">
        {`${ids.length} member${ids.length === 1 ? "" : "s"}`}
      </Text>
    );
  }

  if (surface === "property" && entities.length === 1) {
    return (
      <EntitySelectionHover entity={entities[0]!} disabled={hoverDisabled}>
        <Box shrink minWidth={0}>
          <EntityTag
            entity={entities[0]!}
            weight="regular"
            interactive={false}
          />
        </Box>
      </EntitySelectionHover>
    );
  }

  return (
    <EntityGroupTag
      entities={entities}
      variant="stack"
      size={surface === "property" ? "sm" : "xs"}
      interactive={false}
      hoverDisabled={hoverDisabled}
    />
  );
}
